import { Search } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export type PickerRole = {
  key: string;
  label: string;
  description: string;
  group: string;
};

export type PickerGroup = { key: string; label: string };

/** Why a role cannot be ticked or unticked here, keyed by role. */
export type PickerLocks = Record<string, string | undefined>;

/** A short note shown under a role in place of its description, keyed by role. */
export type PickerNotes = Record<string, string | undefined>;

/** Roles whose labels or descriptions match the search, kept in their groups and in registry order. */
export function groupRolesForPicker(roles: PickerRole[], groups: PickerGroup[], query: string): Array<{ group: PickerGroup; roles: PickerRole[] }> {
  const needle = query.trim().toLowerCase();
  const matches = roles.filter((role) => !needle || role.label.toLowerCase().includes(needle) || role.description.toLowerCase().includes(needle));
  return groups.map((group) => ({ group, roles: matches.filter((role) => role.group === group.key) })).filter((entry) => entry.roles.length > 0);
}

/**
 * Grouped checkboxes for Studio roles, each with its one-line description and
 * a search box. Used by Edit roles and by Invite, so every role on the page is
 * picked the same way and a new role needs no new UI.
 */
export function RolePicker({
  roles,
  groups,
  selected,
  onToggle,
  locks = {},
  notes = {},
  disabled = false,
}: {
  roles: PickerRole[];
  groups: PickerGroup[];
  selected: ReadonlySet<string>;
  onToggle: (key: string, checked: boolean) => void;
  locks?: PickerLocks;
  notes?: PickerNotes;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const id = useId();
  const grouped = useMemo(() => groupRolesForPicker(roles, groups, query), [groups, query, roles]);

  return (
    <div className="space-y-4">
      {roles.length > 6 ? (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Find a role"
            aria-label="Find a role"
            className="pl-9 text-base sm:text-sm"
          />
        </div>
      ) : null}
      {grouped.length === 0 ? <p className="text-sm text-muted-foreground">No role matches.</p> : null}
      {grouped.map(({ group, roles: groupRoles }) => (
        <fieldset key={group.key} className="space-y-1.5">
          <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</legend>
          {groupRoles.map((role) => {
            const lock = locks[role.key];
            const checked = selected.has(role.key);
            const inputId = `${id}-${role.key}`;
            return (
              <label
                key={role.key}
                htmlFor={inputId}
                title={lock}
                className={cn(
                  'flex items-start gap-3 rounded-lg border border-border px-3 py-2.5',
                  lock || disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-muted/40',
                  checked && 'border-primary/40 bg-primary/5',
                )}
              >
                <input
                  id={inputId}
                  type="checkbox"
                  checked={checked}
                  disabled={Boolean(lock) || disabled}
                  onChange={(event) => onToggle(role.key, event.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary disabled:cursor-not-allowed"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-foreground">{role.label}</span>
                  <span className="block text-xs leading-5 text-muted-foreground">{lock ?? role.description}</span>
                  {!lock && notes[role.key] ? <span className="block text-xs leading-5 text-warning-foreground">{notes[role.key]}</span> : null}
                </span>
              </label>
            );
          })}
        </fieldset>
      ))}
    </div>
  );
}
